import { createContext, memo, ReactNode, useContext, useEffect, useState } from 'react';
import { ChildrenProps, FlexBox } from '@dfl/mui-react-common';
import PageLayout, { PageLayoutProps } from 'layouts/PageLayouts/PageLayout';
import { Paper, Stack, styled, Typography } from '@mui/material';

type PagePaperLayoutProps = ChildrenProps & {
  title?: string;
  subtitle?: string;
  nPadding?: boolean;
  generalAction?: ReactNode;
  helpButton?: ReactNode;
};

export const nPaddingSx = {};
export const sx = {
  padding: {
    xs: 1,
    sm: 2,
    xl: 4,
  },
  paddingTop: {
    xs: 1,
    sm: 2,
    xl: 3,
  },
};

export const GeneralActions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 14,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    gap: 8,
  },
}));

interface ActionPortalContext {
  render: boolean;
}

export const ActionPortal = createContext<ActionPortalContext>({
  render: false,
}); // component props type

const PagePaperLayout = ({
  children,
  title,
  subtitle,
  nPadding,
  helpButton,
  ...layoutProps
}: PagePaperLayoutProps & PageLayoutProps) => {
  const [render, setRendered] = useState(false);

  useEffect(() => {
    setRendered(true);
  }, []);

  return (
    <PageLayout {...layoutProps}>
      <Paper sx={{ position: 'relative', ...(nPadding ? nPaddingSx : sx) }}>
        {helpButton}
        <FlexBox justifyContent={'space-between'} alignItems={{ xs: 'flex-start', md: 'center' }} gap={2} flexWrap={'wrap'} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Stack flex={1}>
            <Typography variant={'h1'}>{title}</Typography>
            {subtitle && <Typography variant={'body1'}>{subtitle}</Typography>}
          </Stack>
          <GeneralActions id={'page-general-actions'}></GeneralActions>
        </FlexBox>
        <ActionPortal.Provider
          value={{
            render,
          }}
        >
          {children}
        </ActionPortal.Provider>
      </Paper>
    </PageLayout>
  );
};

export const useGeneralActionPortal = () => {
  const context = useContext(ActionPortal);

  if (!context) {
    throw new Error('You need to be inside PagePaperLayout to use "GeneralActions"');
  }

  return context.render;
};

export default memo(PagePaperLayout);
